import { signal, computed } from '@preact/signals';
import type { AlternateEnclosure } from '../../shared/types';
import { currentEpisode, isLive, liveItem } from './player';

export interface StreamDiagnostics {
  // Runtime metrics
  activeSourceUrl: string;
  sourceOrigin: string;
  detectedMimeType: string;
  codec: string;
  bitrateKbps: number | null;
  bufferSeconds: number;
  bufferHealthPct: number;
  playbackState: 'playing' | 'paused' | 'buffering' | 'idle';
  isLiveStream: boolean;

  // Declared Feed metadata
  enclosureType: string;
  enclosureLength: number | null;
  durationSec: number | null;
  alternateEnclosures: AlternateEnclosure[];
}

export const streamInspectorOpen = signal(false);
export const runtimeMimeType = signal<string>('');
export const runtimeBitrate = signal<number | null>(null);

let activeAudioEl: HTMLAudioElement | null = null;

export function registerDiagnosticsAudio(el: HTMLAudioElement) {
  activeAudioEl = el;

  const probeMetadata = () => {
    if (!el.src) return;
    try {
      const url = new URL(el.src, window.location.href);
      const ext = url.pathname.split('.').pop()?.toLowerCase();
      if (ext === 'opus') runtimeMimeType.value = 'audio/ogg; codecs=opus';
      else if (ext === 'aac' || ext === 'm4a') runtimeMimeType.value = 'audio/mp4';
      else if (ext === 'mp3') runtimeMimeType.value = 'audio/mpeg';
      else if (ext === 'flac') runtimeMimeType.value = 'audio/flac';
      else if (ext === 'm3u8') runtimeMimeType.value = 'application/x-mpegURL';
    } catch {}
  };

  el.addEventListener('loadedmetadata', probeMetadata);
  el.addEventListener('play', probeMetadata);
}

export const activeDiagnostics = computed<StreamDiagnostics | null>(() => {
  const ep = currentEpisode.value;
  const live = liveItem.value;
  const isLiveActive = isLive.value;

  if (!ep && !live) return null;

  const activeUrl = activeAudioEl?.src || ep?.enclosureUrl || live?.enclosureUrl || '';
  let sourceOrigin = '';
  try {
    if (activeUrl) sourceOrigin = new URL(activeUrl, window.location.href).hostname;
  } catch {}

  // Calculate buffer seconds
  let bufferSec = 0;
  if (activeAudioEl && activeAudioEl.buffered.length > 0) {
    const curTime = activeAudioEl.currentTime;
    for (let i = 0; i < activeAudioEl.buffered.length; i++) {
      const start = activeAudioEl.buffered.start(i);
      const end = activeAudioEl.buffered.end(i);
      if (curTime >= start && curTime <= end) {
        bufferSec = Math.max(0, end - curTime);
        break;
      }
    }
  }

  // Derive codec & bitrate from alternate enclosures or primary enclosure
  let detectedCodec = 'Unknown';
  let bitrate: number | null = runtimeBitrate.value;
  let mimeType = runtimeMimeType.value || ep?.enclosureType || live?.enclosureType || 'audio/mpeg';

  if (ep?.alternateEnclosures && ep.alternateEnclosures.length > 0) {
    const match = ep.alternateEnclosures.find((alt) =>
      alt.sources.some((s) => s.uri === activeUrl)
    ) || ep.alternateEnclosures[0];

    if (match) {
      if (match.codecs) detectedCodec = match.codecs;
      if (match.bitrate) bitrate = Math.round(match.bitrate / 1000);
      if (match.type) mimeType = match.type;
    }
  }

  if (detectedCodec === 'Unknown') {
    if (mimeType.includes('opus')) detectedCodec = 'Opus';
    else if (mimeType.includes('mp4') || mimeType.includes('aac') || mimeType.includes('m4a')) detectedCodec = 'AAC';
    else if (mimeType.includes('mpeg') || mimeType.includes('mp3')) detectedCodec = 'MP3';
    else if (mimeType.includes('flac')) detectedCodec = 'FLAC';
    else if (mimeType.includes('ogg')) detectedCodec = 'Vorbis / Ogg';
    else if (mimeType.includes('mpegURL') || activeUrl.includes('.m3u8')) detectedCodec = 'HLS Stream';
  }

  // Fallback bitrate calculation from length & duration if known
  if (!bitrate && ep?.enclosureLen && ep?.durationSec && ep.durationSec > 0) {
    bitrate = Math.round((ep.enclosureLen * 8) / ep.durationSec / 1000);
  }

  return {
    activeSourceUrl: activeUrl,
    sourceOrigin,
    detectedMimeType: mimeType,
    codec: detectedCodec,
    bitrateKbps: bitrate,
    bufferSeconds: Math.round(bufferSec * 10) / 10,
    bufferHealthPct: Math.min(100, Math.round((bufferSec / 30) * 100)),
    playbackState: activeAudioEl?.paused ? 'paused' : 'playing',
    isLiveStream: isLiveActive,
    enclosureType: ep?.enclosureType || live?.enclosureType || 'audio/mpeg',
    enclosureLength: ep?.enclosureLen || null,
    durationSec: ep?.durationSec || null,
    alternateEnclosures: ep?.alternateEnclosures || [],
  };
});
